import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import layoutStyles from '../AppLayout/AppLayout.module.scss';
import styles from './styles.module.scss';
import { UserStore, RootStore, BookingDictionariesStore, BookingCCStore } from 'AppClient/stores';
import { inject, observer } from 'mobx-react';
import { Spin, Tooltip } from 'components/antd';
import { Fragment } from 'components';
import { isEmpty, find } from 'lodash-es';
import { ThemeContext } from 'app-themes';
import classNames from 'classnames';
import UserProfileModal from 'AppClient/components/EditUserProfile';
import { getPhoneCodeByCountryId } from 'utils/utils';
import CreditCardsSection from './СreditCardsSection';
import UserProfileBox from './UserProfileBox';
import AlternativeContactsSection from './AlternativeContactsSection';
import {
	NotificationByEmailDisplay,
	ReferenceFieldsDisplayList,
	ExternalLink,
	Status,
} from 'components';

interface Props {
	userStore?: UserStore;
	bookingDictionariesStore?: BookingDictionariesStore;
	bookingCCStore?: BookingCCStore;
}

@inject(({ userStore, bookingDictionariesStore, bookingCCStore }: RootStore) => ({
	userStore,
	bookingDictionariesStore,
	bookingCCStore,
}))
@observer
export class UserProfile extends Component<Props> {
	static contextType = ThemeContext;

	getPreferredServiceType() {
		const { userInfo } = this.props.userStore!;
		if (userInfo === null) {
			return false;
		}
		const { findCarById } = this.props.bookingDictionariesStore!;
		const { preferredCarTypeId } = userInfo;
		const preferredCarType = preferredCarTypeId
			? findCarById(Number(preferredCarTypeId))
			: null;

		if (preferredCarType) {
			return preferredCarType.carTypeName;
		}

		return false;
	}

	getLinkWithTooltip(linkPrefix: string, payload: string, tooltipText: string) {
		return (
			<Tooltip title={tooltipText} placement="bottom">
				<span>
					<ExternalLink href={linkPrefix + ':' + payload} text={payload} />
				</span>
			</Tooltip>
		);
	}

	getPhoneWithCode = (phoneCodeCountryId: number, phoneNumber: string) => {
		const { countriesDictionary } = this.props.bookingDictionariesStore!;
		return `${getPhoneCodeByCountryId(countriesDictionary, phoneCodeCountryId)} ${phoneNumber}`;
	};

	getReferenceFields() {
		const { userInfo, customerInfoData } = this.props.userStore!;

		if (!userInfo || !customerInfoData) {
			return [];
		}

		const referenceFields = userInfo.referenceFields.map(field => {
			const referenceField = find(
				customerInfoData.referenceFields,
				item => item.id === field.customerReferenceFieldId
			);

			return {
				referenceFieldName: (referenceField && referenceField.name) || '',
				value: field.defaultValue,
				customerReferenceFieldId: field.customerReferenceFieldId,
			};
		});

		return referenceFields;
	}

	render() {
		const { customerInfoData, isIdle, isPending, isSuccess, userInfo } = this.props.userStore!;
		const { isCreditCardUser } = this.props.bookingCCStore!;
		const { isCityFetching } = this.props.bookingDictionariesStore!;
		const hasData = !isEmpty(customerInfoData) && isSuccess;
		const preferredServiceType = this.getPreferredServiceType();
		const referenceFields = this.getReferenceFields();
		const theme = this.context;

		if (userInfo === null) {
			return userInfo;
		}

		return (
			<Fragment>
				<Helmet>
					<title>User profile</title>
				</Helmet>
				<Spin spinning={isIdle || isPending || isCityFetching}>
					<div
						className={classNames(styles.userProfile, {
							[styles[`userProfile-${theme}`]]: theme !== '',
						})}
					>
						<div className={layoutStyles.appWrapper}>
							<div className={styles.userItem}>
								<div className={styles.userPic} />
								<div className={styles.userContent}>
									<div className={styles.userTopLine}>
										<h2 className={styles.userName}>
											{userInfo.firstName} {userInfo.lastName}
											{hasData && (
												<Status status={'darkInfo'}>
													{customerInfoData!.name}
												</Status>
											)}
										</h2>
										<UserProfileModal />
									</div>
									<div className={styles.userRow}>
										<UserProfileBox title="Personal details">
											<ul className={styles.userList}>
												<li>
													{this.getLinkWithTooltip(
														'tel:',
														this.getPhoneWithCode(
															userInfo.phoneCodeCountryId,
															userInfo.phoneNumber
														),
														'Phone'
													)}
												</li>
												<li>
													{this.getLinkWithTooltip(
														'mailto:',
														userInfo.emailAddress,
														'E-mail'
													)}
												</li>
												{userInfo.formattedAddress && (
													<li>{userInfo.formattedAddress}</li>
												)}
											</ul>
										</UserProfileBox>
										<UserProfileBox title="Notifications by email">
											<NotificationByEmailDisplay
												bookingConfirmation={userInfo.bookingConfirmation}
												invoiceConfirmation={userInfo.invoiceConfirmation}
												calendarBookingEvent={userInfo.calendarBookingEvent}
											/>
										</UserProfileBox>
										{!isEmpty(referenceFields) && (
											<UserProfileBox title="Reference fields">
												<ReferenceFieldsDisplayList
													referenceFields={referenceFields}
												/>
											</UserProfileBox>
										)}
										{preferredServiceType || userInfo.journeyNotes ? (
											<UserProfileBox title="Trip details">
												<ul>
													{preferredServiceType && (
														<li>
															<span className={styles.userListLabel}>
																Preferred service type:{' '}
															</span>
															{preferredServiceType}
														</li>
													)}
													{userInfo.journeyNotes && (
														<li>
															<span className={styles.userListLabel}>
																Notes to driver:{' '}
															</span>
															{userInfo.journeyNotes}
														</li>
													)}
												</ul>
											</UserProfileBox>
										) : null}
									</div>
								</div>
							</div>
						</div>
					</div>
					<AlternativeContactsSection
						alternativeContactList={userInfo.alternativeContactList}
						getLinkWithTooltip={this.getLinkWithTooltip}
						getPhoneWithCode={this.getPhoneWithCode}
					/>
					{isCreditCardUser && <CreditCardsSection />}
				</Spin>
			</Fragment>
		);
	}
}

export default UserProfile;
